import { useState } from 'react'
import { View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Head from 'expo-router/head'
import {
  dashboardInfoQueryOptions,
  exerciseStatsQueryOptions,
} from '@/src/queries/activity-queries'
import {
  exerciseGoalQueryOptions,
  exerciseWeekQueryOptions,
} from '@/src/queries/exercise-queries'
import { warmupWeekdayCompletionQueryOptions } from '@/src/queries/warmup-queries'
import { getCurrentYearWeek } from '@/src/utils/year-week'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { AppScreenTopBar } from '../../../components/AppScreenTopBar'
import { PullToRefreshScrollView } from '../../../components/ui/PullToRefreshScrollView'
import { QuerySuspenseBoundary } from '../../../components/ui/QuerySuspenseBoundary'
import { SectionErrorState } from '../../../components/ui/SectionErrorState'
import { colors } from '../../../theme'
import { ExerciseGoalCard } from '../components/ExerciseGoalCard'
import { ExerciseGoalEditModal } from '../components/ExerciseGoalEditModal'
import {
  StatisticsCards,
  StatisticsCardsSkeleton,
} from '../components/statistics-cards/StatisticsCards'
import { LatestVoiceAssessmentSection } from '../components/voice-assessment/LatestVoiceAssessmentSection'
import {
  STATISTICS_CONTENT_PADDING_BOTTOM,
  STATISTICS_CONTENT_PADDING_X,
} from '../constants/statistics.styles'
import { useStatisticsScreenController } from '../logic/useStatisticsScreenController'

export default function StatisticsScreen() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['common', 'statistics'])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const insets = useSafeAreaInsets()
  const yearWeek = getCurrentYearWeek()

  const {
    exerciseGoal,
    isModalVisible,
    selectedGoalValue,
    isSavingGoal,
    modalTitle,
    openExerciseGoalModal,
    closeExerciseGoalModal,
    handleSelectExerciseGoal,
  } = useStatisticsScreenController()

  async function handleRefresh() {
    setIsRefreshing(true)
    try {
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: warmupWeekdayCompletionQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
        queryClient.refetchQueries({
          queryKey: dashboardInfoQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
        queryClient.refetchQueries({
          queryKey: exerciseStatsQueryOptions.queryKey,
          exact: true,
        }),
        queryClient.refetchQueries({
          queryKey: exerciseWeekQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
        queryClient.refetchQueries({
          queryKey: exerciseGoalQueryOptions().queryKey,
          exact: true,
        }),
      ])
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <View
      className="flex-1"
      style={{ backgroundColor: colors.backgroundPrimary }}
    >
      <Head>
        <title>{t('common:page_title_statistics')}</title>
      </Head>
      <AppScreenTopBar />
      <PullToRefreshScrollView
        refreshing={isRefreshing}
        onRefresh={handleRefresh}
        refreshControlProps={{
          colors: [colors.primary],
          tintColor: colors.primary,
        }}
        className="flex-1"
        scrollEventThrottle={16}
        contentContainerStyle={{
          paddingHorizontal: STATISTICS_CONTENT_PADDING_X,
          paddingBottom: STATISTICS_CONTENT_PADDING_BOTTOM + insets.bottom,
        }}
        showsVerticalScrollIndicator={false}
      >
        <QuerySuspenseBoundary
          loadingFallback={<StatisticsCardsSkeleton />}
          errorFallback={({ reset }) => (
            <SectionErrorState
              testID="statistics-cards-error"
              title={t('statistics:metrics_error_title')}
              message={t('statistics:metrics_error_message')}
              onRetry={reset}
            />
          )}
        >
          <StatisticsCards />
        </QuerySuspenseBoundary>
        <ExerciseGoalCard
          {...exerciseGoal}
          onEditPress={openExerciseGoalModal}
        />
        <LatestVoiceAssessmentSection />
        <ExerciseGoalEditModal
          visible={isModalVisible}
          selectedValue={selectedGoalValue}
          onSelectValue={handleSelectExerciseGoal}
          onRequestClose={closeExerciseGoalModal}
          title={modalTitle}
          isSaving={isSavingGoal}
        />
      </PullToRefreshScrollView>
    </View>
  )
}
